/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Product Offerings Modal Success component
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.10.0
 */

import { useCallback, useMemo } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { IWizardOrder } from '@/main/modules/wizard/wizardTypes';
import { Button, LinkBlock } from '@components/common';
import { appHistoryPush } from '@modules/app/appHistory';
import {
    makeSelectTranslationWizardProductOfferings,
    makeSelectWizardTranslation,
} from '@modules/internationalization/intSelectors';
import { IWizardProductOfferingsTranslation } from '@modules/internationalization/types';
import { IWizardTranslation } from '@modules/internationalization/types/wizards/wizard';
import { IModalPopup } from '@modules/modal/components/ModalPopup';
import {
    ModalPopupContainerStyled,
    ModalPopupControlContainer,
    ModalPopupTextStyled,
    ModalPopupTitleStyled,
} from '@modules/modal/components/styled/ModalStyled';
import { guardComponentType } from '@modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '@modules/role/selectors/guardSelector';
import { getPageOrderIdUrl, isPageOrderIdExternalUrl } from '@pages/PageOrderId/utils/getPageOrderIdUrl';
import { EProductsCategoryUrl } from '@pages/PageProducts/tariffsAndServicesConstants';
import { IRootReducer } from '@reducers/rootReducer';
import { RoutePath } from '@routes/constants/routesConstants';

interface IProductOfferingsModalSuccessProps {
    isDisconnect: boolean;
    isTariff: boolean;
    isMegaPowersAvailable: boolean;
    orderId: IWizardOrder['orderId'] | IWizardOrder[];
}

type TProductOfferingsModalSuccess = IProductOfferingsModalSuccessProps & IModalPopup & IConnectedProps;

/**
 * Use this modal only if megaPowers feature flag is disabled or < 2.
 * For orders use ModalMultipleOrders instead
 * @deprecated
 */
const ProductOfferingsModalSuccess = ({
    componentsFeature,
    dataName,
    isMegaPowersAvailable,
    isDisconnect,
    isTariff,
    onCloseModal,
    orderId,
    wizardProductOfferingsTranslation,
    wizardTranslation,
}: TProductOfferingsModalSuccess) => {
    const onButtonClick = useCallback(() => {
        onCloseModal();
        appHistoryPush(RoutePath.main);
    }, [onCloseModal]);

    const modalBodyContent = useMemo(
        () =>
            isMegaPowersAvailable
                ? {
                      text: wizardTranslation.wizardModalSummary.megapowers,
                      route: `${RoutePath.products}#${EProductsCategoryUrl.MEGAPOWERS}`,
                      linkText: wizardProductOfferingsTranslation.wizardModalSuccess.productsPage,
                  }
                : {
                      text: wizardTranslation.wizardModalSummary.details,
                      route: RoutePath.orders,
                      linkText: wizardProductOfferingsTranslation.wizardModalSuccess.orderPage,
                  },
        // translation deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMegaPowersAvailable],
    );

    const handleLinkClick = useCallback(() => {
        onCloseModal();
        appHistoryPush(modalBodyContent.route);
    }, [modalBodyContent.route, onCloseModal]);

    return (
        <ModalPopupContainerStyled data-component={'modal-popup'} data-name={dataName} width="490px">
            <ModalPopupTitleStyled data-name="modalPopupTitle">
                {wizardTranslation.wizardModalSummary.orderMessageStart}&nbsp;
                {Array.isArray(orderId) ? (
                    <LinkBlock
                        isExternalUrl={isPageOrderIdExternalUrl(componentsFeature)}
                        key={orderId[0]?.orderId}
                        url={getPageOrderIdUrl(componentsFeature, orderId[0]?.orderId)}
                    >
                        № {orderId[0]?.orderId}
                    </LinkBlock>
                ) : (
                    <LinkBlock
                        isExternalUrl={isPageOrderIdExternalUrl(componentsFeature)}
                        url={getPageOrderIdUrl(componentsFeature, orderId)}
                    >
                        № {orderId}
                    </LinkBlock>
                )}
                &nbsp;
                {isDisconnect
                    ? wizardProductOfferingsTranslation.wizardModalSuccess.orderMessageEndDisconnectService
                    : isTariff
                    ? wizardProductOfferingsTranslation.wizardModalSuccess.orderMessageEndConnectTariff
                    : wizardProductOfferingsTranslation.wizardModalSuccess.orderMessageEndConnectService}
            </ModalPopupTitleStyled>
            <ModalPopupTextStyled data-name="modalPopupText">
                {modalBodyContent.text} &nbsp;
                <LinkBlock
                    dataName="product-offering-orders-link"
                    url={modalBodyContent.route}
                    onClick={handleLinkClick}
                >
                    {modalBodyContent.linkText}
                </LinkBlock>
            </ModalPopupTextStyled>
            <ModalPopupControlContainer>
                <Button dataName="closeButton" onClick={onButtonClick} onMouseDown={onButtonClick}>
                    {wizardProductOfferingsTranslation.wizardModalSuccess.mainPage}
                </Button>
            </ModalPopupControlContainer>
        </ModalPopupContainerStyled>
    );
};

interface IConnectedProps {
    componentsFeature: guardComponentType;
    wizardProductOfferingsTranslation: IWizardProductOfferingsTranslation;
    wizardTranslation: IWizardTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    wizardProductOfferingsTranslation: makeSelectTranslationWizardProductOfferings(),
    wizardTranslation: makeSelectWizardTranslation(),
});

export default connect(mapStateToProps)(ProductOfferingsModalSuccess);
